import { gql, useQuery } from '@apollo/client';
import type { FC, ReactNode } from 'react';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import type { AsyncFnReturn } from 'react-use/lib/useAsync';

import { ButtonV3Icon } from '../../components/Button/ButtonV3Icon';
import { Tooltip } from '../../components/GraphModal/Tooltip';
import { ReactComponent as FacebookIcon } from '../../icons/icon-button-facebook.svg';
import { ReactComponent as LinkedinIcon } from '../../icons/icon-button-linked-in.svg';
import { ReactComponent as XIcon } from '../../icons/icon-button-x.svg';
import { ReactComponent as LinkIcon } from '../../icons/icon-link.svg';
import type { QueryType } from '../../types/api';
import { copyToClipboard } from '../../utils/clipboard';
import { openShareWindow } from '../../utils/share';

const messages = defineMessages({
  shareOn: { defaultMessage: 'Share on {socialNetwork}', id: 'W3i7UR' },
  copyLinkTooltip: {
    defaultMessage: 'Copy the link to this discussion to the clipboard',
    id: 'KY+3pY',
  },
  errorMessage: { defaultMessage: 'An error has occurred', id: 'h4hXDR' },
});

const socialNameToLabelMap = {
  facebook: 'Facebook',
  twitter: 'X',
  linkedin: 'LinkedIn',
};

const getTwitterUsernameQuery = gql`
  query GetTwitterUserName {
    me {
      id
      profile {
        id
        twitterUsername
        status
      }
    }
  }
`;
export type ShareCommentProps = {
  subject?: string;
  content: string;
  shortUrl: AsyncFnReturn;
};
export const ShareComment: React.FC<ShareCommentProps> = ({ shortUrl, subject, content }) => {
  const { data } = useQuery<QueryType>(getTwitterUsernameQuery, {
    fetchPolicy: /* frontConfig.disableCache ? 'network-only' : */ 'cache-first',
  });
  const { twitterUsername } = data?.me?.profile || {};
  const yakliRef = useRef<HTMLInputElement>(null);

  const [{ value, loading, error }, fetch] = shortUrl;

  const copyLinkOnClick = useCallback(() => {
    copyToClipboard(value);
    yakliRef.current?.select();
  }, [value]);

  useEffect(() => {
    if (!value && !loading && !error) fetch();
  }, [fetch, value, loading, error]);

  const val: string = value || '';

  return (
    (loading && <div className="loader" />) ||
    (error && <div>something went wrong</div>) || (
      <div
        className="bg-white pt-[19px] pb-[15px] px-[7px] space-y-2 md:space-y-4"
        style={{ minWidth: 150 }}
      >
        <Tooltip content={<FormattedMessage {...messages.copyLinkTooltip} />}>
          <div
            className="flex justify-center items-center h-8 bg-gray-100 rounded-full cursor-pointer hover:text-primary-base px-3 space-x-2"
            role="button"
            tabIndex={0}
            onClick={copyLinkOnClick}
          >
            <LinkIcon className="w-3 h-3 text-gray-360" />
            <input
              className="outline-none appearance-none bg-transparent text-center pointer-events-none"
              ref={yakliRef}
              value={val}
              style={{ width: `${val.length - 5}ch` }}
              readOnly
            />
          </div>
        </Tooltip>
        <div className="flex justify-center space-x-2 md:space-x-4">
          <SocialShare
            name="facebook"
            icon={<FacebookIcon />}
            subject={subject}
            content={content}
            twitterUsername={twitterUsername}
            value={value}
          />
          <SocialShare
            name="twitter"
            icon={<XIcon />}
            subject={subject}
            content={content}
            twitterUsername={twitterUsername}
            value={value}
          />
          <SocialShare
            name="linkedin"
            icon={<LinkedinIcon />}
            subject={subject}
            content={content}
            twitterUsername={twitterUsername}
            value={value}
          />
        </div>
      </div>
    )
  );
};

type SocialName = 'facebook' | 'twitter' | 'linkedin';

interface SocialShareProps {
  name: SocialName;
  icon: ReactNode;
  subject: string | undefined;
  content: string;
  twitterUsername: string | undefined;
  value: string;
}

const SocialShare: FC<SocialShareProps> = memo(function SocialShare(props) {
  const { name, icon, subject, content, twitterUsername, value } = props;
  const { formatMessage } = useIntl();
  const socialShareOnClick = useCallback(() => {
    openShareWindow({ subject, content, network: name, twitterUsername, shortUrl: value });
  }, [subject, content, name, twitterUsername, value]);
  return (
    <Tooltip
      content={
        <FormattedMessage
          {...messages.shareOn}
          values={{ socialNetwork: socialNameToLabelMap[name] }}
        />
      }
    >
      <ButtonV3Icon
        icon={icon}
        className="hover:shadow"
        radius="rounded-full"
        noPadding
        onClick={socialShareOnClick}
        aria-label={formatMessage(messages.shareOn, { socialNetwork: name })}
      />
    </Tooltip>
  );
});
